import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import {
    GifPreview,
    LinkPreview,
    Mood,
    Reactions,
    ReactionsComponent,
    Threads,
    TimelineItem,
    TimelineItemType
} from "@teammood/timeline";
import { MoodsService } from "../moods.service";
import { LinkyModule } from "@teammood/shared/util/linky";
import { GifPreviewComponent } from "../../../../shared/ui/gifpreview/gifpreview";
import { ThreadsComponent } from "../../../threads/threads.component";
import { AsyncPipe, NgClass } from "@angular/common";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { distinctUntilChanged, map } from "rxjs";
import { TranslateModule } from "@ngx-translate/core";
import { MatTooltip } from "@angular/material/tooltip";
import { LinkPreviewComponent } from "../../../../shared/ui/linkpreview/linkpreview";
import { EditMoodBoxComponent } from "../edit-mood-box/edit-mood-box.component";
import { MatMenuModule } from "@angular/material/menu";
import { UserInfos } from "../../../../shared/ui/username-icon/user-infos.type";
import { ReviewBadgeComponent } from "../../review-badge/review-badge.component";
import { ManagerPanelComponent } from "../../manager-panel/manager-panel.component";

@Component({
    selector: "tm-mood-card",
    templateUrl: "./mood-card.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        LinkyModule,
        GifPreviewComponent,
        NgClass,
        AsyncPipe,
        TranslateModule,
        MatTooltip,
        LinkPreviewComponent,
        ReactionsComponent,
        ThreadsComponent,
        EditMoodBoxComponent,
        MatMenuModule,
        ReviewBadgeComponent,
        ManagerPanelComponent
    ]
})
export class MoodCardComponent implements OnChanges {

    @Input({required: true})
    mood: Mood;

    @Input() isOdd: boolean;

    @Input() userInfos: UserInfos;

    protected icon: string;

    protected showTimelineItemOptions: boolean;

    protected moodState: 'moodToFill' |'moodEdit' | 'moodComplete' | 'hideMood';

    @Output()
    onEdit: EventEmitter<Mood> = new EventEmitter();

    @Output()
    onDelete: EventEmitter<Mood> = new EventEmitter();

    @Output()
    onModerate: EventEmitter<Mood> = new EventEmitter();

    @ViewChild('threadsComponent')
    private threadsComponent: ThreadsComponent;

    protected showThreads: boolean = true;

    protected isHandset$ =  this.breakpointObserver.observe([
        Breakpoints.Handset,
    ]).pipe(
        map(result => result.matches),
        distinctUntilChanged()
    )

    constructor(private moodsService: MoodsService, private breakpointObserver: BreakpointObserver) {
    }

    private setMoodState() {
        if (this.mood.comment === null || this.mood.comment.body === '') {
            if (this.mood.isEditable) {
                this.moodState = "moodToFill";
            } else {
                this.moodState = "hideMood";
            }
        } else if (this.mood.comment && this.mood.comment.body !== '') {
            this.moodState = "moodComplete";
        } else {
            this.moodState = "hideMood";
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        this.setMoodState();
        // build icon at loading or when day is reloaded after edition
        if(!changes.mood?.previousValue || changes.mood.currentValue !== changes.mood?.previousValue) {
            this.icon = this.moodsService.build_icon(this.mood.mood, this.mood.date);
        }
    }

    protected moodBeforeUpdate: Mood;

    protected editMood() {
        if (this.mood.isEditable) {
            this.moodBeforeUpdate = structuredClone(this.mood);
            this.moodState = "moodEdit";
        }
    }

    protected onMoodEdited(updatedMood: Mood) {
        if (this.mood.isEditable) {
            this.onEdit.emit(updatedMood);
            this.setMoodState();
        }
    }

    protected cancelEdit() {
        this.mood = structuredClone(this.moodBeforeUpdate);
        this.setMoodState();
    }

    protected deleteMood() {
        if (this.mood.isDeletable) {
            this.onDelete.emit(this.mood);
        }
    }

    protected reportMood() {
        if (!this.mood.isDeletable && !this.mood.isEditable) {
            this.onModerate.emit(this.mood);
        }
    }

    protected onThreadsUpdated(timelineItem: TimelineItem & Threads): void {
        if (timelineItem.itemType == "moodItem"){
            this.mood = timelineItem as Mood;
        }
    }

    protected onReacted(timelineItem: {id: string, itemType: TimelineItemType} & Reactions): void {
        if (timelineItem.itemType == "moodItem"){
            this.mood = timelineItem as Mood;
        }
    }

    protected toggleThreads() {
        this.showThreads = !this.showThreads;
    }

    protected discuss() {
        if (this.threadsComponent) {
            this.threadsComponent.discuss = true;
        }
    }

    toGifPreview(linkPreview: LinkPreview): GifPreview {
        return new GifPreview((linkPreview));
    }

    // fix a race condition by using a function
    getThreadsComponent: () => ThreadsComponent = () => this.threadsComponent;
}